import { inject, Injector, Service } from '@angular/core';
import { CurrentUser } from '@core/user/current-user';
import { WebSocketActionEnum } from '@shared/enums/websocket';
import { WebSocketMessage, CommandArgs, LiveOutbound, UserOutbound } from '@shared/models/websocket';
import { LiveSession } from '../live/live-session';
import { WebSocketClient } from './web-socket-client';

export interface LiveCommandTarget {
  /** Defaults to the current live code. A key given as undefined is sent as is. */
  destinationId?: string | null;
  /** Defaults to the signed-in user. A key given as undefined is sent as is. */
  sourceId?: string | null;
  /** Only sent when given. */
  liveCode?: string | null;
}

/** The only place that builds the messages sent to the other participants of a live. */
@Service()
export class LiveCommand {
  // Read lazily: LiveSession and the services it injects send live commands themselves.
  private readonly injector = inject(Injector);
  private readonly webSocketClient = inject(WebSocketClient);
  private readonly userService = inject(CurrentUser);

  /** Live control channel (pause, record, mute, approvals…). */
  send<A extends keyof LiveOutbound>(action: A, ...[params, target = {}]: CommandArgs<LiveOutbound[A], LiveCommandTarget>): Promise<void> {
    return this.webSocketClient.sendMessage(this.build(action, params, target));
  }

  sendDraw<A extends keyof LiveOutbound>(action: A, ...[params, target = {}]: CommandArgs<LiveOutbound[A], LiveCommandTarget>): Promise<void> {
    return this.webSocketClient.sendDrawEventMessage(this.build(action, params, target));
  }

  sendChat<A extends keyof LiveOutbound>(action: A, ...[params, target = {}]: CommandArgs<LiveOutbound[A], LiveCommandTarget>): Promise<void> {
    return this.webSocketClient.SendChatMessage(this.build(action, params, target));
  }

  /** Sends to one participant only. */
  sendToUser<A extends keyof UserOutbound>(userId: string, action: A, ...[params]: CommandArgs<UserOutbound[A], never>): Promise<void> {
    return this.webSocketClient.sendUserMessage({ ...this.build(action, params, { destinationId: userId }), audience: 'USER' } as WebSocketMessage);
  }

  private build(action: WebSocketActionEnum, params: unknown, target: LiveCommandTarget): WebSocketMessage {
    const message = {
      audience: 'LIVE',
      sourceId: 'sourceId' in target ? target.sourceId : this.userService.currentUserId(),
      destinationId: 'destinationId' in target ? target.destinationId : this.injector.get(LiveSession).currentLive()?.code,
      message: params === undefined ? { action } : { action, params },
    } as WebSocketMessage;
    return 'liveCode' in target ? ({ ...message, liveCode: target.liveCode } as WebSocketMessage) : message;
  }
}
